import { type CheckedState } from '@radix-ui/react-checkbox'
import { FolderSearchIcon } from 'lucide-react'

import { Button } from '@/components/button/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/popover'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/sheet/sheet'

import { type ClientPortalRelevantCaseItem } from '@/lib/queries/case-dashboard/types'
import { cn } from '@/lib/utils'

import { CaseCheckboxList } from './case-checkbox-list'
import { useSelectOtherCasesButton } from './use-select-other-cases-button'

export interface SelectOtherCasesButtonProps {
  dataAllApplicantCases?: ClientPortalRelevantCaseItem[]
  dataProUserSelectedCases?: ClientPortalRelevantCaseItem[]
  isDisabledAllCheckbox?: boolean
  beingUpdatedCaseId?: number
  onCheckedChange: (caseId: number, checked: CheckedState) => void
}

export const SelectOtherCasesButton = ({
  dataAllApplicantCases,
  dataProUserSelectedCases,
  beingUpdatedCaseId,
  isDisabledAllCheckbox,
  onCheckedChange,
}: SelectOtherCasesButtonProps) => {
  const { windowWidth } = useSelectOtherCasesButton()

  return (
    <>
      {windowWidth < 1024 ? (
        <Sheet>
          <SheetTrigger asChild>
            <Button type="button" variant="secondary" size="sm" className={cn('flex gap-x-2')}>
              <FolderSearchIcon size={16} />
              Select Other Cases
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom">
            <SheetHeader>
              <SheetTitle className={cn('text-left')}>Select Other Cases</SheetTitle>
            </SheetHeader>
            {dataAllApplicantCases && (
              <>
                <div
                  className={cn(
                    'text-xs font-normal text-foreground',
                    'bg-elevated',
                    'rounded-[10px]',
                    'p-2.5',
                    'w-[300px] max-w-full',
                    'my-5'
                  )}
                >
                  Select other cases to create corresponding portals, allowing you to navigate and customize.
                </div>
                <CaseCheckboxList
                  beingUpdatedCaseId={beingUpdatedCaseId}
                  isDisabledAllCheckbox={isDisabledAllCheckbox}
                  dataAllApplicantCases={dataAllApplicantCases}
                  dataProUserSelectedCases={dataProUserSelectedCases}
                  onCheckedChange={onCheckedChange}
                />
              </>
            )}
          </SheetContent>
        </Sheet>
      ) : (
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="secondary" size="sm" className={cn('flex gap-x-2')}>
              <FolderSearchIcon size={16} />
              Select Other Cases
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className={cn('w-max')}>
            {dataAllApplicantCases && (
              <>
                <div
                  className={cn(
                    'text-xs font-normal text-foreground',
                    'bg-elevated',
                    'rounded-[10px]',
                    'p-2.5',
                    'w-[300px]',
                    'mb-5'
                  )}
                >
                  Select other cases to create corresponding portals, allowing you to navigate and customize.
                </div>
                <CaseCheckboxList
                  beingUpdatedCaseId={beingUpdatedCaseId}
                  isDisabledAllCheckbox={isDisabledAllCheckbox}
                  dataAllApplicantCases={dataAllApplicantCases}
                  dataProUserSelectedCases={dataProUserSelectedCases}
                  onCheckedChange={onCheckedChange}
                />
              </>
            )}
          </PopoverContent>
        </Popover>
      )}
    </>
  )
}
